import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface LinkHealth {
  wanLinkId: string;
  name: string;
  role: string;
  transport: string;
  state: string;
  usable: boolean;
  reason: string;
  withheld: string | null;
  faultKind: string | null;
  faultSummary: string | null;
  latencyMs: number | null;
  jitterMs: number | null;
  loss: number | null;
  heldDownUntil: string | null;
  flapPenalty: number;
  probeTargets: string[];
  hasEnoughTargets: boolean;
}

const SEVERITY: Record<string, 'ok' | 'warn' | 'bad'> = {
  UP: 'ok',
  DEGRADED: 'warn',
  HOLD_DOWN: 'warn',
  SUPPRESSED: 'warn',
  DOWN: 'bad',
  UNKNOWN: 'warn',
};

/**
 * WAN health for one edge.
 *
 * Every link shows why it is in its state, not just the state. A failover moves
 * customer traffic and the question afterwards is always why; answering it on
 * the page is cheaper than answering it in a support call.
 *
 * "Carrying traffic" is shown separately from the state because the two are not
 * the same: a degraded link is still carrying traffic, and an operator deciding
 * whether to intervene needs the second fact more than the first.
 */
export function WanHealthPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<LinkHealth[]>(`/wan/devices/${deviceId}/health`),
    [deviceId],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data || data.length === 0) {
    return (
      <div className="card">
        <h2>{t('wanHealth.title')}</h2>
        <EmptyState hint={t('wanHealth.empty')} />
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('wanHealth.title')}</h2>

      {data.map((link) => (
        <div key={link.wanLinkId} className="card card--nested">
          <div className="page-head">
            <h3>
              {link.name} <span className="muted">({link.transport})</span>
            </h3>
            <span className={`chip chip--${SEVERITY[link.state] ?? 'warn'}`}>
              {t(`wanHealth.state.${link.state}`)}
            </span>
          </div>

          {/* Distinct from the state: a degraded link is still carrying traffic,
              which is what decides whether to intervene. */}
          <p>{link.usable ? t('wanHealth.usable') : t('wanHealth.notUsable')}</p>
          <p className="muted">{link.reason}</p>

          {link.faultKind && link.faultSummary ? (
            <Banner variant="warning">
              <strong>
                {t('wanHealth.fault')}: {t(`wanHealth.signal.${link.faultKind}`)}
              </strong>
              {/* The summary names the layer, which is the actionable part — an
                  operator can fix a gateway, not "3 of 5 targets down". */}
              <div>{link.faultSummary}</div>
            </Banner>
          ) : null}

          {link.withheld ? (
            <Banner variant="info">
              <strong>{t('wanHealth.withheld')}</strong>
              <div>{link.withheld}</div>
            </Banner>
          ) : null}

          {!link.hasEnoughTargets ? (
            <Banner variant="warning">{t('wanHealth.tooFewTargets')}</Banner>
          ) : null}

          <dl className="kv">
            <dt>{t('wanHealth.latency')}</dt>
            <dd>{link.latencyMs === null ? '—' : `${Math.round(link.latencyMs)} ms`}</dd>
            <dt>{t('wanHealth.jitter')}</dt>
            <dd>{link.jitterMs === null ? '—' : `${Math.round(link.jitterMs)} ms`}</dd>
            <dt>{t('wanHealth.loss')}</dt>
            <dd>{link.loss === null ? '—' : `${(link.loss * 100).toFixed(1)}%`}</dd>
            <dt>{t('wanHealth.probeTargets')}</dt>
            <dd>{link.probeTargets.length === 0 ? '—' : link.probeTargets.join(', ')}</dd>
            {link.heldDownUntil ? (
              <>
                <dt>{t('wanHealth.heldDownUntil')}</dt>
                <dd>{new Date(link.heldDownUntil).toLocaleString()}</dd>
              </>
            ) : null}
          </dl>
        </div>
      ))}
    </div>
  );
}
